import React from 'react';
import { Award, Users, Heart, Shield } from 'lucide-react';
import { aboutImage, whyChooseUs, clinicInfo } from '../mock';

export const About = () => {
  const iconMap = {
    Users: Users,
    Cpu: Award,
    Heart: Heart,
    DollarSign: Shield
  };

  return (
    <section id="about" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            About Our Clinic
          </h2>
          <div className="w-24 h-1 bg-emerald-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Committed to providing exceptional dental care with a personal touch
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Image Side */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={aboutImage} 
                alt="Dr. Aruna" 
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/50 to-transparent"></div>
            </div>
            {/* Floating Card */}
            <div className="absolute -bottom-8 -right-8 bg-white p-6 rounded-xl shadow-xl max-w-xs">
              <div className="flex items-center gap-4">
                <div className="bg-emerald-100 p-3 rounded-full">
                  <Award size={32} className="text-emerald-700" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">15+</div>
                  <div className="text-sm text-gray-600">Years of Excellence</div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Side */}
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Welcome to Aruna's Advanced Dental Clinic
            </h3>
            
            {/* Doctor Info */}
            <div className="bg-emerald-50 p-6 rounded-xl mb-6 border-l-4 border-emerald-600">
              <h4 className="text-2xl font-bold text-emerald-800 mb-2">
                {clinicInfo.doctor.name}
              </h4>
              <p className="text-lg text-emerald-700 font-semibold mb-1">
                {clinicInfo.doctor.qualification}
              </p>
              <p className="text-emerald-600 mb-1">
                {clinicInfo.doctor.specialization}
              </p>
              <p className="text-gray-700">
                {clinicInfo.doctor.experience} of experience in advanced dental care
              </p>
            </div>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              At Aruna's Advanced Dental Clinic, we believe everyone deserves a healthy, beautiful smile. 
              Led by {clinicInfo.doctor.name}, an experienced {clinicInfo.doctor.specialization} with {clinicInfo.doctor.experience} of expertise, 
              our team combines state-of-the-art technology with compassionate care to deliver exceptional results.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              From routine check-ups to complex procedures like laser root canals and full mouth rehabilitation, 
              we offer comprehensive dental solutions tailored to your unique needs. Our modern facility in Balapur 
              is designed to make you feel comfortable and relaxed throughout your visit.
            </p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              We're not just about fixing teeth – we're about building lasting relationships with our 
              patients and helping you achieve optimal oral health for life.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg">
                  <Shield size={24} className="text-emerald-700" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Safe & Sterile</div>
                  <div className="text-sm text-gray-600">ISO Certified</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg">
                  <Heart size={24} className="text-emerald-700" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Patient Care</div>
                  <div className="text-sm text-gray-600">Top Priority</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="mt-20">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Why Choose Us
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item) => {
              const Icon = iconMap[item.icon] || Award;
              return (
                <div 
                  key={item.id}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
                >
                  <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                    <Icon size={32} className="text-emerald-700" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3 text-center">
                    {item.title}
                  </h4>
                  <p className="text-gray-600 text-center leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
